import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { NewInvitationContent } from '../components/new-invitation/NewInvitationContent';
import {
  useAcceptInvitation,
  useGetNursingHomeList,
  useRejectInvitation,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack } from '@mui/material';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface NewInvitationPageProps {}

export const NewInvitationPage: FC<NewInvitationPageProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant');
  const nursingHomeId = searchParams.get('nursingHomeId');
  const globalServiceProviderId = searchParams.get('globalServiceProviderId');
  const { mutateAsync: acceptInvitation } = useAcceptInvitation();
  const { mutateAsync: rejectInvitation } = useRejectInvitation();
  const { data: nursingHomeList, isLoading: isNursingHomeListLoading } =
    useGetNursingHomeList();

  if (!tenant || !globalServiceProviderId || !nursingHomeId) {
    throw new Error('Missing required parameters for invitation');
  }

  // The nursing home is not there if a) the nursing home does not exist or b) the invitation is not valid anymore
  const nursingHome = useMemo(() => {
    return nursingHomeList?.find(
      (nursingHome) => nursingHome.nursingHomeId === nursingHomeId
    );
  }, [nursingHomeList, nursingHomeId]);

  const onAcceptInvitation = useCallback(async () => {
    if (nursingHome) {
      await acceptInvitation({
        nursingHomeId: nursingHome.nursingHomeId,
        globalServiceProviderId,
        tenant,
      });
      enqueueSnackbar(
        t('service-provider-app.new-invitation.invitation-accepted'),
        { variant: 'success' }
      );
    } else {
      // If the nursing home is not there, it either does not exist (unlikely) or the invitation is not valid anymore.
    }
    navigate(spAppMainPages.HOME);
  }, [
    t,
    acceptInvitation,
    enqueueSnackbar,
    nursingHome,
    navigate,
    globalServiceProviderId,
    tenant,
  ]);

  const onRejectInvitation = useCallback(async () => {
    if (nursingHome) {
      await rejectInvitation({
        nursingHomeId: nursingHome.nursingHomeId,
        globalServiceProviderId,
        tenant,
      });
      enqueueSnackbar(
        t('service-provider-app.new-invitation.invitation-rejected'),
        { variant: 'info' }
      );
    } else {
      // Nursing home does not exist or invitation is not valid anymore
    }
    navigate(spAppMainPages.HOME);
  }, [
    rejectInvitation,
    enqueueSnackbar,
    nursingHome,
    globalServiceProviderId,
    tenant,
    t,
    navigate,
  ]);

  if (isNursingHomeListLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          height: '100%',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
        }}
      >
        <CircularProgress color="primary" variant="indeterminate" />
      </Stack>
    );
  }

  return (
    <NewInvitationContent
      loading={isNursingHomeListLoading}
      accountAffiliationName={nursingHome?.nursingHomeName}
      connectionStatus={nursingHome?.connectionStatus}
      onAcceptInvitation={onAcceptInvitation}
      onRejectInvitation={onRejectInvitation}
    />
  );
};
